import React from "react";
import { NavigateFunction } from "react-router";
import { useSelector } from "react-redux";
import { UserPermissions } from "../../permissions";
import { PermissionType } from "../../gqltypes";
import { links } from "../links";
import { Loading } from "../../Common";
import { StoreState } from "../../store";

const selectAppPermissions = (state: StoreState) => state.app.permissions;

// Define the expected props, including navigate from withRouter
interface PropsWithNavigate {
  navigate: NavigateFunction;
}

const withoutPermissions = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  isOrg: boolean,
  requiredPermissions: PermissionType[]
) => {
  // Functional component expects props P & the navigate prop
  const WithoutPermissionsComponent: React.FC<P & PropsWithNavigate> = (
    props
  ) => {
    // Extract navigate and restProps directly
    const { navigate, ...restProps } = props;
    const userPermissions = useSelector(selectAppPermissions);

    const hasRequiredPermissions = React.useCallback(() => {
      if (isOrg) {
        const hasOrgPerms = userPermissions?.org
          ? requiredPermissions.every(
              (permission) => userPermissions.org[permission]
            )
          : false;

        return hasOrgPerms;
      } else {
        const hasSomePerms = userPermissions?.some
          ? requiredPermissions.every(
              (permission) => userPermissions.some[permission]
            )
          : false;

        return hasSomePerms;
      }
    }, [userPermissions]);

    if (!userPermissions) {
      return <Loading />;
    }

    if (!hasRequiredPermissions()) {
      // Use the navigate prop directly
      navigate(links.admin.noPermission());
      return null;
    }

    // Pass original props down AND the userPermissions fetched via useSelector
    return (
      <WrappedComponent {...(props as P)} userPermissions={userPermissions} />
    );
  };

  // Remove the WithNavigate wrapper, return the checking component directly
  return WithoutPermissionsComponent;
};

export default withoutPermissions;
